import type { ShiftIncentiveData } from "@/pages/incentive/models/ShiftIncentiveData";
import { useAssetId } from "@/setup/hooks/useAssetId";
import { STACKER_PRODUCTION_LABEL } from "@/setup/mill-config/mill-config";
import { routeDelaysManager } from "@/setup/routes/delay.routes";
import { routeMillGantt } from "@/setup/routes/mill.routes";
import { routeShiftReport } from "@/setup/routes/shift_report.routes";
import { KPICard, KPIThresholds } from "@/shared/components/KPICard";
import type { KPIData } from "@/shared/models/KpiData";
import {
	actualTons,
	actualTonsTooltip,
	productivityDuringUptime,
	targetTons,
	targetTonsTooltip,
} from "@/shared/models/KpiData";
import { pageBgColor } from "@/utils/theme";
import {
	ButtonWithLoading,
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	routeLogbookEntry,
	useHasPermission,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Box, Button, Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GanttChart } from "../mill/gantt/components/GanttChart";
import {
	Delay,
	DmhEvent,
	Production,
	RollChange,
} from "../mill/gantt/models/GanttReponse";
import { IncentiveCard } from "./components/IncentiveCard";
import { ProductionStats } from "./components/ProductionStats";
import { PushRateCrewChart } from "./components/PushRateCrewChart";
import { RollingTimeCard } from "./components/RollingTimeCard";
import { ShiftReportSkeleton } from "./components/ShiftReportSkeleton";

import { TableDelays } from "./components/tables/delays/TableDelays";
import { TableHeats } from "./components/tables/heats/TableHeats";
import { TableLogbook } from "./components/tables/logbook/TableLogbook";
import { TableProductionSummary } from "./components/tables/production-summary/TableProductionSummary";
import { TableRollChanges } from "./components/tables/roll-changes/TableRollChanges";
import { TableStackerBundles } from "./components/tables/stacker/TableStackerBundles";
import {
	ShiftReport,
	stackerPendingReview,
} from "./models/ShiftReportResponse";
import { getShiftReport } from "./repositories/ShiftReportRepository";
import { incentiveDashboardPermission } from "@/setup/routes/incentive.routes";

export const useShiftReport = (shiftId: number | null) => {
	return useQuery<ShiftReport, Error>({
		queryKey: ["shiftReport", shiftId],
		meta: { realtime: ["M2.GetShiftReport"] },
		queryFn: ({ signal }) => getShiftReport({ shiftId: shiftId! }, signal),
		enabled: !!shiftId,
		keepPreviousData: true,
		refetchOnWindowFocus: true,
		refetchInterval: 30_000,
	});
};

const fmtKpi = (
	value: number | null | undefined,
	decimals: number,
	unit: string,
): string => {
	if (value === null || value === undefined) return "-";
	return `${parseFloat(value.toFixed(decimals))} ${unit}`;
};

const kpiCards = (d: KPIData) => [
	{
		title: "TPH",
		value: d.TPH,
		unit: "TPH",
		percentage: d.TPHPct,
		subtitle: "Actual Avg",
		tooltip: "Rolled Tons / Hours Actual",
		thresholds: {
			red: d.TPHRed,
			orange: d.TPHOrange,
			yellow: d.TPHYellow,
			greenSoft: d.TPHGreenSoft,
			greenNormal: d.TPHGreenNormal,
			greenHard: d.TPHGreenHard,
		} as KPIThresholds,
		details: [
			{
				label: "Target",
				value: fmtKpi(d.TPHTarget, 2, "TPH"),
				tooltip: "Target TPH from the matched production event",
			},
			{
				label: "TPH Avg. Last Hour",
				value: fmtKpi(d.TPHAvgLastHour, 2, "TPH"),
				tooltip: "Average TPH based on tons rolled in the last 60 minutes.",
			},
		],
	},
	{
		title: "Uptime",
		value: d.Uptime,
		unit: "Hrs.",
		percentage: d.UptimePct,
		subtitle: "Uptime - Mills On",
		tooltip: "Hours Actual - Delay Hours",
		thresholds: {
			red: d.UptimeRed,
			orange: d.UptimeOrange,
			yellow: d.UptimeYellow,
			greenSoft: d.UptimeGreenSoft,
			greenNormal: d.UptimeGreenNormal,
			greenHard: d.UptimeGreenHard,
		} as KPIThresholds,
		details: [
			{
				label: "Hours Available",
				value: fmtKpi(d.HoursAvailable, 2, "Hrs"),
				tooltip: "Total elapsed shift hours.",
			},
			{
				label: "Uptime %",
				value: fmtKpi(d.UptimePercentage, 1, "%"),
				tooltip: "Uptime Hours / Hours Actual x 100.",
			},
			{
				label: "Avg. TPH during Uptime",
				value: fmtKpi(d.AvgTPHDuringUptime, 1, "TPH"),
				tooltip: "Rolled Tons / Uptime Hours",
			},
		],
	},
	{
		title: "Push Rate",
		value: d.PushRateWithDelays,
		unit: "Billets",
		percentage: d.PushRatePct,
		subtitle: "Avg. Per Hour",
		tooltip: "Average billets per hour during the shift",
		thresholds: {
			red: d.PushRateRed,
			orange: d.PushRateOrange,
			yellow: d.PushRateYellow,
			greenSoft: d.PushRateGreenSoft,
			greenNormal: d.PushRateGreenNormal,
			greenHard: d.PushRateGreenHard,
		} as KPIThresholds,
		details: [
			{
				label: "Avg. Billets/h during Uptime",
				value: fmtKpi(d.PushRateActual, 1, ""),
				tooltip: "Billets per uptime hour (excl. delays).",
			},
			{
				label: "Scheduled Push Rate",
				value: fmtKpi(d.PushRateScheduled, 1, ""),
			},
		],
	},
	{
		title: "Productivity",
		value: d.ProductivityOverTarget,
		unit: "%",
		percentage: d.ProductivityOverTarget,
		subtitle: "Overall Productivity",
		thresholds: {
			red: d.ProductivityRed,
			orange: d.ProductivityOrange,
			yellow: d.ProductivityYellow,
			greenSoft: d.ProductivityGreenSoft,
			greenNormal: d.ProductivityGreenNormal,
			greenHard: d.ProductivityGreenHard,
		} as KPIThresholds,
		details: [
			{
				label: "Actual Tons",
				value: actualTons(d),
				tooltip: actualTonsTooltip(d, "shift"),
			},
			{
				label: "Target Tons",
				value: targetTons(d),
				tooltip: targetTonsTooltip(d, "shift"),
			},
			{
				label: "Productivity during Uptime",
				value: fmtKpi(productivityDuringUptime(d), 1, "%"),
				tooltip:
					"Avg TPH during Uptime / Target TPH x 100. Pace while actually rolling, with delays excluded.",
			},
		],
	},
];

const incentiveThresholds = (d: ShiftIncentiveData): KPIThresholds => ({
	red: d.IncRed,
	orange: d.IncOrange,
	yellow: d.IncYellow,
	greenSoft: d.IncGreenSoft,
	greenNormal: d.IncGreenNormal,
	greenHard: d.IncGreenHard,
});

const defaultIncentiveThresholds: KPIThresholds = {
	red: 0,
	orange: 40,
	yellow: 60,
	greenSoft: 75,
	greenNormal: 85,
	greenHard: 95,
};

export const ShiftReportPage = () => {
	const [isLoadingPDF, setIsLoadingPDF] = useState(false);
	const [error, setError] = useState("");
	const hasPermissionTo = useHasPermission();
	const canViewIncentive = hasPermissionTo(incentiveDashboardPermission);
	const [hoveredHeatId, setHoveredHeatId] = useState<number | null>(null);
	const [hoveredDelayId, setHoveredDelayId] = useState<number | null>(null);
	const [hoveredSectionId, setHoveredSectionId] = useState<number | null>(null);
	const navigate = useNavigate();
	const assetId = useAssetId();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const { shiftId: shiftIdParam } = useParams();
	const shiftId = get(shiftInfo, "CurrentShiftId", null) as number | null;
	const shiftIdRef = useRef<number | null>(null);
	shiftIdRef.current = shiftId;

	const {
		data,
		isLoading,
		error: e,
		isError,
		refetch,
	} = useShiftReport(shiftId);

	const handleCrewShiftNavigate = useCallback(
		async (selectedShiftId: number) => {
			if (!assetId) return;

			navigate(`${routeShiftReport}/${selectedShiftId}`);
			await getShiftDataFromAPI(null, selectedShiftId, assetId);
		},
		[assetId, getShiftDataFromAPI, navigate],
	);

	const handleGeneratePDF = async () => {
		try {
			setIsLoadingPDF(true);

			const shiftId = shiftInfo?.CurrentShiftId;
			if (!shiftId) {
				setError("No valid shift selected");
				return;
			}

			const response = await fetch(
				`/api/shift-reports/pdf/${shiftId}?includeIncentive=${canViewIncentive}`,
				{ method: "POST" },
			);

			if (!response.ok) {
				const text = await response.text();
				throw new Error(text || "Error generating the PDF");
			}

			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			window.open(url, "_blank", "noopener,noreferrer");
			// Clean up the object URL after a delay to allow the browser to load it
			setTimeout(() => URL.revokeObjectURL(url), 10_000);
		} catch (err: any) {
			setError(err?.message || "Error generating the PDF");
		} finally {
			setIsLoadingPDF(false);
		}
	};

	useEffect(() => {
		if (shiftInfo) {
			const newId = get(shiftInfo, "CurrentShiftId", null);
			// Only navigate when the shift navigator moves to a DIFFERENT shift
			// than the URL already shows — avoids a navigate→fetch→navigate loop.
			if (String(newId) !== shiftIdParam) {
				navigate(`${routeShiftReport}/${newId}`);
			}
			(async () => {
				await refetch();
			})();
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (!assetId) return;
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				await getShiftDataFromAPI(null, shiftId, assetId);
			})();
		} else {
			(async () => {
				await getShiftDataFromAPI(null, 0, assetId);
			})();
		}
	}, [assetId]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	if (!data) {
		return <ShiftReportSkeleton />;
	}

	if (isError) {
		return <ErrorModal error={error} onHide={() => setError("")} />;
	}

	return (
		<>
			<HelmetDexteel title="Shift Report" />
			<Grid2
				container
				spacing={1}
				style={{ padding: "10px 20px", backgroundColor: pageBgColor }}
			>
				<Grid2
					container
					size={12}
					justifyContent="space-between"
					alignItems="center"
				>
					<Grid2 size={{ md: 4 }} alignItems="center">
						<Typography
							variant="h5"
							sx={{
								fontWeight: 600,
								userSelect: "none",
							}}
						>
							Shift Report
						</Typography>
					</Grid2>
					<Grid2
						elevation={1}
						size={{ md: 4 }}
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							p: 1,
							mb: 1,
						}}
						component={Paper}
					>
						<Box sx={{ display: "inline-flex" }}>
							<ShiftDayNavigatorControl
								preventInit
								withPaper={false}
								assetId={assetId}
							/>
						</Box>
					</Grid2>
					<Grid2
						size={{ md: 4 }}
						container
						justifyContent="flex-end"
						alignItems="center"
					>
						<ButtonWithLoading
							variant="contained"
							color="primary"
							onClick={handleGeneratePDF}
							isLoading={isLoadingPDF}
							sx={{ whiteSpace: "nowrap", minWidth: 150 }}
						>
							Generate PDF
						</ButtonWithLoading>
					</Grid2>
				</Grid2>
				{/* Row 1: Summary + KPIs + Incentive + PushRate + RollingTime */}
				<Grid2 container size={12} spacing={1} sx={{ alignItems: "stretch" }}>
					<Grid2 size={{ xs: 3.25 }} component={Paper} sx={{ display: "flex" }}>
						<Box padding={2} sx={{ width: "100%" }}>
							<Typography
								variant="subtitle1"
								style={{ fontWeight: "600" }}
								marginBottom={0.5}
							>
								Shift Summary
							</Typography>
							<ProductionStats data={data.ShiftStats} />
						</Box>
					</Grid2>

					{data.KpiData &&
						kpiCards(data.KpiData).map((card) => (
							<Grid2
								key={card.title}
								size={{
									xs: canViewIncentive ? 1.25 : 1.45,
								}}
								sx={{ display: "flex", minWidth: 0, overflow: "hidden" }}
							>
								<KPICard {...card} />
							</Grid2>
						))}

					{canViewIncentive && (
						<Grid2
							size={{ xs: 1.25 }}
							sx={{ display: "flex", minWidth: 0, overflow: "hidden" }}
						>
							<IncentiveCard
								data={data.IncentiveData}
								thresholds={
									data.IncentiveData
										? incentiveThresholds(data.IncentiveData)
										: defaultIncentiveThresholds
								}
							/>
						</Grid2>
					)}

					<Grid2
						size={{ xs: canViewIncentive ? 1.25 : 1.475 }}
						sx={{ display: "flex", minWidth: 0, overflow: "hidden" }}
					>
						<PushRateCrewChart
							data={data.PushRateData}
							kpiData={data.KpiData}
							onShiftNavigate={handleCrewShiftNavigate}
						/>
					</Grid2>

					<Grid2
						size={{ xs: canViewIncentive ? 1.25 : 1.475 }}
						sx={{ display: "flex", minWidth: 0, overflow: "hidden" }}
					>
						<RollingTimeCard data={data.RollingTime} />
					</Grid2>
				</Grid2>

				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2} style={{ position: "relative" }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography variant="h6" style={{ fontWeight: "600" }}>
								Gantt
							</Typography>
							<Button
								size="small"
								variant="text"
								onClick={() =>
									window.open(`${routeMillGantt}/${shiftId}`, "_blank")
								}
							>
								See on Gantt
							</Button>
						</Box>

						{data.GanttDelays && data.GanttProduction && (
							<GanttChart
								isLoading={isLoading}
								refetch={refetch}
								start={shiftInfo?.CurrentStart!}
								end={shiftInfo?.CurrentEnd!}
								productionData={data.GanttProduction as Production[]}
								delayData={data.GanttDelays as Delay[]}
								rollChangeData={data.RollChangeIntervals as RollChange[]}
								dmhEventData={data.DmhEvents as DmhEvent[]}
								compactMode
								onHeatHover={setHoveredHeatId}
								hoveredHeatId={hoveredHeatId}
								onDelayHover={setHoveredDelayId}
								hoveredDelayId={hoveredDelayId}
								onSectionHover={setHoveredSectionId}
								hoveredSectionId={hoveredSectionId}
							/>
						)}
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Typography
							variant="h6"
							style={{ fontWeight: "600" }}
							marginBottom={2}
						>
							Production Summary
						</Typography>
						<Grid2 height="20vh">
							<TableProductionSummary
								productionSummary={data.ProductionSummary}
								hoveredSectionId={hoveredSectionId}
								onSectionHover={setHoveredSectionId}
							/>
						</Grid2>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", textAlign: "right", mt: 0.5 }}
						>
							Values in parentheses represent the full campaign totals,
							including production outside this shift.
						</Typography>
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Typography
							variant="h6"
							style={{ fontWeight: "600" }}
							marginBottom={2}
						>
							Heats
						</Typography>
						<Grid2 height="30vh">
							<TableHeats
								heats={data.Heats}
								hoveredHeatId={hoveredHeatId}
								onHeatHover={setHoveredHeatId}
							/>
						</Grid2>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", textAlign: "right", mt: 0.5 }}
						>
							Values in parentheses represent the full campaign totals,
							including production outside this shift.
						</Typography>
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Typography
							variant="h6"
							style={{ fontWeight: "600" }}
							marginBottom={2}
						>
							{STACKER_PRODUCTION_LABEL}
						</Typography>
						<Grid2 height="30vh">
							<TableStackerBundles bundles={data.StackerBundles} />
						</Grid2>
						{(() => {
							const pending = stackerPendingReview(data.StackerBundles || []);
							return pending.total > 0 ? (
								<Typography
									variant="caption"
									color="warning.dark"
									sx={{ display: "block", textAlign: "right", mt: 0.5 }}
									data-testid="shift-report-stacker-pending"
								>
									* {pending.total} {pending.total === 1 ? "bundle" : "bundles"}{" "}
									awaiting review on Stacker Production
								</Typography>
							) : null;
						})()}
					</Box>
				</Grid2>

				{((data.UninstalledCarriers && data.UninstalledCarriers.length > 0) ||
					(data.InstalledCarriers && data.InstalledCarriers.length > 0) ||
					(data.PassChanges && data.PassChanges.length > 0)) && (
					<Grid2 size={{ xs: 12 }} component={Paper}>
						<Box padding={2}>
							<Typography
								variant="h6"
								style={{ fontWeight: "600" }}
								marginBottom={2}
							>
								Carrier Changes
							</Typography>
							<TableRollChanges
								uninstalledCarriers={data.UninstalledCarriers}
								installedCarriers={data.InstalledCarriers}
								passChanges={data.PassChanges}
							/>
						</Box>
					</Grid2>
				)}

				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 2,
							}}
						>
							<Typography variant="h6" style={{ fontWeight: "600" }}>
								Top 5 Delays
							</Typography>
							<Button
								size="small"
								variant="text"
								onClick={() =>
									window.open(
										`${routeDelaysManager}/${shiftId}?assetId=12`,
										"_blank",
									)
								}
							>
								See on Delays
							</Button>
						</Box>
						<Grid2 height="30vh">
							<TableDelays
								delays={data.Delays}
								hoveredDelayId={hoveredDelayId}
								onDelayHover={setHoveredDelayId}
							/>
						</Grid2>
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12 }} component={Paper}>
					<Box padding={2}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 2,
							}}
						>
							<Typography variant="h6" style={{ fontWeight: "600" }}>
								Logbook
							</Typography>
							<Button
								size="small"
								variant="text"
								onClick={() =>
									window.open(`${routeLogbookEntry}/${shiftId}`, "_blank")
								}
							>
								See on Logbook
							</Button>
						</Box>
						<Grid2 height="35vh">
							<TableLogbook />
						</Grid2>
					</Box>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
